import { createRoot } from 'react-dom/client'
import { useWallet } from '~/lib/useWallet'
import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'
import { BrowserRouter as Router, Navigate, useRoutes } from 'react-router-dom'
import {
	QueryClient,
	QueryClientProvider,
} from '@tanstack/react-query'
import { WagmiProvider } from 'wagmi'
import { RainbowKitProvider, darkTheme } from '@rainbow-me/rainbowkit'
import { wagmiConfig } from '~/lib/wagmiConfig'
import './index.pcss'
import 'symbiosis-uikit/style.css'
import '@rainbow-me/rainbowkit/styles.css'
import { EarnType } from '~/lib/constants'
import StakePage from '~/pages/StakePage'
import {
	earnTypeByChainId,
	earnTypeByPathName,
	pathToEarnPage,
} from '~/lib/routes'
import UnstakePage from '~/pages/UnstakePage'
import ClaimPage from '~/pages/ClaimPage'
import { EarnRoute } from '~/pages/EarnPage'
import DefaultLayout from '~/layouts/DefaultLayout'
import { CONFIG } from '~/lib/config'
import { useMatch } from 'react-router'
import TagManager from 'react-gtm-module'
import { VESISPage } from './pages/VESISPage'
import { CreateLockPage } from './pages/CreateLockPage'
import { ConfirmLockPage } from './pages/ConfirmLockPage'
import { LockSettingsPage } from './pages/LockSettingsPage'
import ClaimRewardsPage from './pages/ClaimRewardsPage/RewardsPage'

Sentry.init({
	dsn: CONFIG.sentry?.dsn,
	integrations: [new Integrations.BrowserTracing()],
	enabled: !!CONFIG.sentry?.dsn,
	release: APP_RELEASE,
	environment: CONFIG.environment,
	tracesSampleRate: 1.0,
})

TagManager.initialize({
	gtmId: 'GTM-WWT64HW',
})

const queryClient = new QueryClient()

const SmartRedirect: React.FC = () => {
	const wallet = useWallet()
	const match = useMatch('/lp-farm/:earnTypeName/:page')
	if (!match) {
		return (
			<Navigate
				to={pathToEarnPage(earnTypeByChainId(wallet.chainId), 'stake')}
			/>
		)
	}
	const {
		params: { earnTypeName },
	} = match
	const earnType = earnTypeByPathName(earnTypeName)
	if (earnType) {
		return <Navigate to={pathToEarnPage(earnType, 'stake')} />
	}
	return <Navigate to="/lp-farm/pancake-swap/stake" />
}

const earnRoutes = (path: string, earnType: EarnType) => {
	return [
		{
			element: <StakePage />,
			path: pathToEarnPage(earnType, 'stake'),
		},
		{
			path: pathToEarnPage(earnType, 'unstake'),
			element: <UnstakePage />,
		},
		{
			path: pathToEarnPage(earnType, 'claim'),
			element: <ClaimPage />,
		},
		{ index: true, element: <SmartRedirect /> },
	]
}

const vesisRoutes = [
	{
		element: <VESISPage />,
		index: true,
	},
	{
		path: 'create',
		element: <CreateLockPage />,
	},
	{
		path: 'confirm',
		element: <ConfirmLockPage />,
	},
	{
		path: 'settings',
		element: <LockSettingsPage />,
	},
]

function App() {
	return useRoutes([
		{
			path: '/',
			element: <DefaultLayout />,
			children: [
				{
					index: true,
					element: <Navigate to="/vesis" />,
				},
				{
					path: 'lp-farm',
					children: [
						{ index: true, element: <SmartRedirect /> },
						{
							path: 'uni-swap',
							element: <EarnRoute earnType={EarnType.uniSwap} />,
							children: earnRoutes(
								'/lp-farm/uni-swap',
								EarnType.uniSwap
							),
						},
						{
							path: 'sushi-swap',
							element: (
								<EarnRoute earnType={EarnType.sushiSwap} />
							),
							children: earnRoutes(
								'/lp-farm/sushi-swap',
								EarnType.sushiSwap
							),
						},
						{
							path: 'pancake-swap',
							element: (
								<EarnRoute earnType={EarnType.pancakeSwap} />
							),
							children: earnRoutes(
								'/lp-farm/pancake-swap',
								EarnType.pancakeSwap
							),
						},
						{ path: '*', element: <SmartRedirect /> },
					],
				},
				{
					path: 'vesis',
					children: [
						{
							element: (
								<Navigate
									to={`/vesis/${CONFIG.vesis[0].slug}`}
									replace
								/>
							),
							index: true,
						},
						...CONFIG.vesis.map((vesisConfig) => {
							return {
								path: vesisConfig.slug,
								children: vesisRoutes,
							}
						}),
					],
				},
				{
					path: 'claim-rewards',
					element: <ClaimRewardsPage />,
				},
			],
		},
		{
			path: '*',
			element: <Navigate to="/crosschain-farm-v2" replace />,
		},
	])
}

createRoot(document.getElementById('application')!).render(
	<WagmiProvider config={wagmiConfig}>
		<QueryClientProvider client={queryClient}>
			<RainbowKitProvider theme={darkTheme()}>
				<Router>
					<App />
				</Router>
			</RainbowKitProvider>
		</QueryClientProvider>
	</WagmiProvider>
)
